import dynamicAPI from './dynamicAPI';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMessage } from '../context/AdminMessageContext';

const url = '/api/auth/products';
const queryKey = 'products';

export default function useProductsHook(props) {
    const { page = 1, id, q = '', limit = 25, machineId = null   } = props;
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    const getProducts = useQuery(
        [queryKey, page, limit],
        async () => {
            const response = await dynamicAPI('get', `${url}?page=${page}&q=${q}&limit=${limit}`);
            if (response.error) {
                const errorMessage = response.error || 'An error occurred while fetching the products.';
                setErrorMessage(errorMessage);
            } else {
                return response;
            }
        },
        {
            retry: 0,
            staleTime: 1000 * 15,
        }
    );

    const getProductsForMachine = useQuery(
        [queryKey, 'machine', machineId, page, limit, q],
        async () => {
            const res = await dynamicAPI(
                'get',
                `${url}?page=${page}&q=${q}&limit=${limit}&machine=${machineId}`
            );
            if (res.error) setErrorMessage(res.error || 'Failed to fetch machine products.');
            return res;
        },
        {
            enabled : !!machineId,
            retry   : 0,
            staleTime: 15_000
        }
    );

    const getProductById = useQuery(
        [queryKey, id],
        async () => {
            const response = await dynamicAPI('get', `${url}/${id}`);
            if (response.error) {
                const errorMessage = response.error || 'An error occurred while fetching the product.';
                setErrorMessage(errorMessage);

            } else {
                return response;
            }
        },
        { retry: 0, enabled: !!id }
    );

    const updateProduct = useMutation(
        async (obj) => {
            const response = await dynamicAPI('put', `${url}/${obj._id}`, obj);
            if (response.isError) throw new Error(response.error);
            return response;
        },
        {
            retry: 0,
            onSuccess: () => {
                setSuccessMessage('Product updated successfully.');
                queryClient.invalidateQueries([queryKey]);
            },
            onError: (error) => {
                setErrorMessage(error.message || 'Failed to update product.');
            }
        }
    );

    const deleteProduct = useMutation(
        async (id) => {
            const response = await dynamicAPI('delete', `${url}/${id}`);
            if (response.isError) throw new Error(response.error);
            return response;
        },
        {
            retry: 0,
            onSuccess: () => {
                setSuccessMessage('Product deleted successfully.');
                queryClient.invalidateQueries([queryKey]);
            },
            onError: (error) => {
                setErrorMessage(error.message || 'Failed to delete product.');
            }
        }
    );

    const postProduct = useMutation(
        async (obj) => {
            const response = await dynamicAPI('post', url, obj);
            if (response.isError) throw new Error(response.error);
            return response;
        },
        {
            retry: 0,
            onSuccess: () => {
                setSuccessMessage('Product created successfully.');
                queryClient.invalidateQueries([queryKey]);
            },
            onError: (error) => {
                setErrorMessage(error.message || 'Failed to create product.');
            }
        }
    );

    return {
        getProducts,
        updateProduct,
        deleteProduct,
        postProduct,
        getProductById,
        getProductsForMachine
    };
}
